/**
 * CAOS-499 — the seam a code-editing component holds its editor behind.
 *
 * Three components in the developer-surfaces group put a person in front of text they type into:
 * `<caos-query-editor>`, `<caos-code-editor>`, and the body pane of `<caos-rest-console>`. Every
 * serious answer for that involves a third-party editing engine, and the specification is explicit
 * that the engine is an implementation detail behind the component's own contract, because the
 * contract is what we promise not to break and the engine is something we may well swap.
 *
 * So the engine is not imported by the components. It is looked up here, at mount time, through
 * {@link codeSurfaceEngine}. That has three consequences worth stating, because they are the reason
 * the indirection is worth its cost rather than ceremony:
 *
 *   • `@caos/ui` ships with NO editor dependency. The built-in engine is a `<textarea>` — every
 *     component below works, is keyboard-accessible and is styled by the tokens with nothing
 *     installed. A library that could not be used until you had chosen an editor would be a library
 *     that forced the choice it is trying to defer.
 *   • An application swaps the engine ONCE, for all three components, by calling
 *     {@link setCodeSurfaceEngine} at start-up. Nothing in any component changes.
 *   • The contract is testable without the engine. A test installs a recording engine and asserts
 *     the component honours the contract — which is the only way to know the seam is real rather
 *     than a type that happens to compile.
 *
 * THE HANDLE IS THE CONTRACT, not the engine object. An engine is asked to mount itself into a host
 * element and hands back a handle; everything the component does afterwards goes through that
 * handle. An engine that keeps per-instance state keeps it in the handle's closure, so two editors
 * on one page cannot reach each other's.
 *
 * WHAT IS DELIBERATELY NOT HERE: syntax highlighting, completion, folding, a theme. Those are
 * things an engine does or does not do, and naming them here would be writing one engine's feature
 * list into the seam every other engine has to pretend to implement. The seam carries only what a
 * component actually calls.
 */

import { buildRegisteredCodeEditorEngine, isCodeEditorEngineRegistered } from './components/code-editor.js';

/** What a mounted editor lets its component do. See the header: this, not the engine, is the contract. */
export interface CodeSurfaceHandle {
  /** What is currently in the editor. */
  getValue(): string;
  /** Replace the contents. MUST NOT raise the change callback — the component already knows. */
  setValue(value: string): void;
  /** Put the caret in the editor. */
  focus(): void;
  /** Turn editing on or off without remounting. */
  setReadOnly(readOnly: boolean): void;
  /** Let go of everything: listeners, observers, whatever the engine attached to the document. */
  destroy(): void;
  /**
   * Find `term` in the contents and reveal it, returning how many times it occurs.
   *
   * OPTIONAL, and the optionality is the point: the three editor destinations all carry a "search
   * the content" control inside the editor on its right edge, and an engine that cannot search is
   * still a usable engine. A component whose engine does not implement this shows the control
   * disabled rather than drawing a control that does nothing.
   */
  search?(term: string): number;
}

/** What a component tells an engine when it asks it to mount. */
export interface CodeSurfaceMountOptions {
  /** The text to start with. */
  readonly value: string;
  /**
   * What kind of text it is — `sql`, `apex`, `json`, `html`, `text`.
   *
   * A HINT, never a promise: an engine that does not know the language shows plain text, which is
   * what the built-in one does for all of them. It is not an enum, because the set of languages an
   * engine understands belongs to the engine.
   */
  readonly language: string;
  readonly readOnly: boolean;
  /** The accessible name for the editing region. Every engine must put it on whatever it focuses. */
  readonly label: string;
  /** Raised whenever the person changes the contents. Never raised by {@link CodeSurfaceHandle.setValue}. */
  readonly onChange: (value: string) => void;
  /**
   * Raised when the person asks to run what they have written — the engine's own run shortcut.
   *
   * Optional on the engine's side; a component always supplies it, because the run button exists
   * either way. An engine with no shortcut simply never calls it.
   */
  readonly onSubmit?: () => void;
}

/** Something that can put an editor inside an element. */
export interface CodeSurfaceEngine {
  /** A name for the engine, so a component can say which one it is drawing and a test can assert it. */
  readonly name: string;
  /** Mount into `host` — which is empty, and inside the component's own shadow root. */
  mount(host: HTMLElement, options: CodeSurfaceMountOptions): CodeSurfaceHandle;
}

/**
 * The engine `@caos/ui` ships with: a plain `<textarea>`.
 *
 * It is not a placeholder to be replaced before the components are usable. It highlights nothing and
 * completes nothing, and it is a real editing surface with real keyboard behaviour, a real
 * accessible name, and the tokens' own monospace face — which is to say every component below works
 * out of the box, and an application that wants highlighting opts INTO a dependency rather than
 * inheriting one.
 *
 * Its `search` reports occurrences and selects the first, which is the honest amount of searching a
 * textarea can do.
 */
export const PLAIN_TEXT_CODE_SURFACE: CodeSurfaceEngine = {
  name: 'plain-text',
  mount(host: HTMLElement, options: CodeSurfaceMountOptions): CodeSurfaceHandle {
    const area = document.createElement('textarea');
    area.className = 'cee-area';
    area.setAttribute('part', 'editor-surface');
    area.spellcheck = false;
    area.value = options.value;
    area.readOnly = options.readOnly;
    area.setAttribute('aria-label', options.label);
    area.setAttribute('data-language', options.language);

    const onInput = (): void => options.onChange(area.value);
    // Ctrl/Cmd+Enter runs it. The two editors that have a run button both want the shortcut, and a
    // person who has just finished typing a query should not have to go and find the button.
    const onKeydown = (e: KeyboardEvent): void => {
      if (e.key === 'Enter' && (e.ctrlKey || e.metaKey) && options.onSubmit) {
        e.preventDefault();
        options.onSubmit();
      }
    };
    area.addEventListener('input', onInput);
    area.addEventListener('keydown', onKeydown);
    host.append(area);

    return {
      getValue: () => area.value,
      setValue: (value: string) => {
        area.value = value;
      },
      focus: () => area.focus(),
      setReadOnly: (readOnly: boolean) => {
        area.readOnly = readOnly;
      },
      destroy: () => {
        area.removeEventListener('input', onInput);
        area.removeEventListener('keydown', onKeydown);
        area.remove();
      },
      search: (term: string): number => {
        if (term === '') return 0;
        const haystack = area.value;
        let count = 0;
        let at = haystack.indexOf(term);
        const first = at;
        while (at !== -1) {
          count += 1;
          at = haystack.indexOf(term, at + term.length);
        }
        if (first !== -1) {
          area.focus();
          area.setSelectionRange(first, first + term.length);
        }
        return count;
      },
    };
  },
};

let installed: CodeSurfaceEngine | null = null;

/**
 * The engine `<caos-code-editor>` is registered to build, adapted to this contract.
 *
 * CAOS-493 shipped the code editor with its own registration point, and an application must have
 * ONE place to install an editor — two would mean installing twice and finding out which surfaces
 * you missed by looking at them. So nothing is registered here separately: with no richer engine
 * installed, these surfaces mount whatever `registerCodeSurfaceEngine` last received, including the
 * plain-text engine the platform ships by default.
 *
 * The two contracts differ in what they promise, which is why this adapter exists rather than one
 * replacing the other. The component seam hands an engine a container and keeps the accessors on
 * the engine; this one returns a HANDLE per mounted surface, because a query editor holds several
 * surfaces at once, and it carries the two things those surfaces need and a lone editor does not: a
 * submit gesture, and an OPTIONAL `search` an engine may decline to implement. An engine that only
 * satisfies the component seam is adapted here and simply has no `search`.
 */
function adoptRegisteredEngine(): CodeSurfaceEngine {
  const built = buildRegisteredCodeEditorEngine();
  return {
    name: built.constructor?.name ?? 'registered',
    mount(container, opts) {
      built.mount(container, {
        initialValue: opts.value,
        readOnly: opts.readOnly,
        language: opts.language,
        onInput: () => opts.onChange(built.getValue()),
        onSave: () => opts.onSubmit?.(),
      });
      if (opts.label !== undefined) container.setAttribute('aria-label', opts.label);
      return {
        getValue: () => built.getValue(),
        setValue: (value: string) => built.setValue(value),
        setReadOnly: (readOnly: boolean) => built.setReadOnly(readOnly),
        focus: () => built.focus(),
        destroy: () => built.destroy(),
      };
    },
  };
}

/**
 * Install the editing engine every code-editing component in this library will mount from here on.
 *
 * Called ONCE at application start-up, before anything is on screen. Components already mounted keep
 * the engine they mounted with — remounting live editors under somebody's cursor would lose their
 * work, and an application that swaps engines mid-session is not a case worth breaking typing for.
 *
 * Passing `null` restores the built-in plain-text engine, which is what a test does to undo itself.
 */
export function setCodeSurfaceEngine(engine: CodeSurfaceEngine | null): void {
  installed = engine;
}

/**
 * The engine a component should mount.
 *
 * A richer engine installed through {@link setCodeSurfaceEngine} wins; otherwise these surfaces
 * defer to whatever `<caos-code-editor>` is registered to build, so an application installs an
 * editor in one place and gets it everywhere.
 */
export function codeSurfaceEngine(): CodeSurfaceEngine {
  if (installed) return installed;
  // An application that registered an editor gets that editor here too — one installation, every
  // surface. Until it does, the platform's default editor stands unclaimed, and these surfaces
  // mount the plain-text surface below instead: it satisfies this contract fully, including the
  // search and the run shortcut a query editor is unusable without.
  return isCodeEditorEngineRegistered() ? adoptRegisteredEngine() : PLAIN_TEXT_CODE_SURFACE;
}

/**
 * The stylesheet text for the built-in engine's surface.
 *
 * It lives here rather than in each component's sheet because the surface belongs to the engine, not
 * to the component drawing it: three components would otherwise each carry a copy of the same rules
 * and drift. A component adopting a different engine simply never matches these selectors.
 */
export const CODE_SURFACE_CSS = `
  .cee-area {
    display: block;
    width: 100%;
    min-height: 120px;
    resize: vertical;
    border: 1px solid var(--caos-color-border);
    border-radius: var(--caos-radius-sm);
    background: var(--caos-color-surface);
    color: var(--caos-color-text);
    font-family: var(--caos-font-mono);
    font-size: 12.5px;
    line-height: 1.55;
    padding: var(--caos-space-3);
    tab-size: 2;
  }
  .cee-area:focus-visible {
    outline: none;
    box-shadow: var(--caos-focus-ring);
  }
  .cee-area[readonly] { background: var(--caos-color-surface-2); }
`;
